import './index.less';
import { debounce } from '../../../modules/utils';

/**
 * 增强浏览器滚动条（可解决滚动条占页面宽度问题）
 */
const Scrollbar = class {
  constructor() {
    // #region 基础属性
    this.x = {
      id: 'hobbit_scrollbar_x',
      el: null,
      last: 0,
      elOffset: 'left',
      elSize: 'width',
      clientOffset: 'clientX',
    };
    this.y = {
      id: 'hobbit_scrollbar_y',
      el: null,
      last: 0,
      elOffset: 'top',
      elSize: 'height',
      clientOffset: 'clientY',
    };
    this.opacity = {
      normal: 0.5, // 激活时滚动条的透明度
      light: 0.15, // 休眠中滚动条的透明度
    };
    this.onMove = false; // 是否鼠标按住滚动条移动中
    this.timingShow = null;
    // #endregion

    // 延时休眠函数
    this.light = debounce(axis => {
      this[axis].el.style.opacity = this.opacity.light;
    }, 800);

    const dd = document.documentElement;
    const db = document.body;
    this.getTotalSize = axis => (axis === 'x'
      ? dd.scrollWidth || db.scrollWidth
      : dd.scrollHeight || db.scrollHeight);
    this.getClientSize = axis => (axis === 'x'
      ? dd.clientWidth || db.clientWidth
      : dd.clientHeight || db.clientHeight);
    this.getScrollSize = axis => (axis === 'x'
      ? window.pageXOffset || dd.scrollLeft || db.scrollLeft
      : window.pageYOffset || dd.scrollTop || db.scrollTop);

    // 滚动条计算展示函数
    this.show = (axis, silent) => {
      if (this.onMove) return;
      const target = this[axis];
      const totalSize = this.getTotalSize(axis);
      const clientSize = this.getClientSize(axis);
      // 自由空余距离，即可以滚动的距离
      const freeSize = totalSize - clientSize;
      if (freeSize <= 0) {
        // 不可滚动了，应隐藏滚动条
        target.el.style.display = 'none';
      } else {
        // 可以滚动，应显示滚动太哦
        target.el.style.display = 'block';
        if (!silent) {
          // 非静默模式下，显示激活样式
          target.el.style.opacity = this.opacity.normal;
          this.light(axis);
        }
        const size = clientSize * (1 - (freeSize / totalSize));
        const offset = (clientSize - size) * (this.getScrollSize(axis) / (totalSize - clientSize));
        target.el.style[target.elOffset] = `${offset}px`;
        target.el.style[target.elSize] = `${size}px`;
      }
    };

    // 鼠标按住滚动条移动
    this.moveTo = (e, axis, end) => {
      if (this.onMove) {
        if (end) {
          this.onMove = false;
          this.light(axis);
          document.removeEventListener('mousemove', axis === 'x' ? this.onMouseMoveX : this.onMouseMoveY);
          document.removeEventListener('mouseup', axis === 'x' ? this.onMouseUpX : this.onMouseUpY);
        }
        const target = this[axis];
        const totalSize = this.getTotalSize(axis);
        const clientSize = this.getClientSize(axis);
        const change = e[target.clientOffset] - target.last;
        target.last = e[target.clientOffset];
        let offset = parseInt(target.el.style[target.elOffset], 10) + change;
        const size = parseInt(target.el.style[target.elSize], 10);
        const max = clientSize - size;
        if (offset < 0) offset = 0;
        else if (offset > max) offset = max;
        target.el.style[target.elOffset] = `${offset}px`;
        const scrollSize = (totalSize - clientSize) * (offset / max);
        if (axis === 'x') {
          window.scrollTo(scrollSize, this.getScrollSize('y'));
        } else {
          window.scrollTo(this.getScrollSize('x'), scrollSize);
        }
      }
    };

    this.onMouseDownX = e => {
      if (e.buttons !== 1) return;
      this.onMove = true;
      this.x.last = e.clientX;
      this.x.el.style.opacity = this.opacity.normal;
      document.addEventListener('mousemove', this.onMouseMoveX);
      document.addEventListener('mouseup', this.onMouseUpX);
      e.preventDefault();
    };

    this.onMouseDownY = e => {
      if (e.buttons !== 1) return;
      this.onMove = true;
      this.y.last = e.clientY;
      this.y.el.style.opacity = this.opacity.normal;
      document.addEventListener('mousemove', this.onMouseMoveY);
      document.addEventListener('mouseup', this.onMouseUpY);
      e.preventDefault();
    };

    this.onMouseMoveX = e => {
      this.moveTo(e, 'x', false);
    };

    this.onMouseMoveY = e => {
      this.moveTo(e, 'y', false);
    };

    this.onMouseUpX = e => {
      this.moveTo(e, 'x', true);
    };

    this.onMouseUpY = e => {
      this.moveTo(e, 'y', true);
    };

    // 滚动事件函数
    this.onScroll = () => {
      this.show('y', false);
    };
  }

  /**
   * 初始化滚动条，仅在非触摸屏环境下启用
   */
  init() {
    // 触摸屏不使用自定滚动条
    if ('ontouchstart' in document.documentElement) return;
    document.body.setAttribute('data-visual-scrollbar', 1);

    const buildScrollbar = () => {
      // 构建x轴滚动条
      this.x.el = document.getElementById(this.x.id);
      if (!this.x.el) {
        this.x.el = document.createElement('div');
        this.x.el.setAttribute('id', this.x.id);
        document.body.appendChild(this.x.el);
        this.x.el.addEventListener('mousedown', this.onMouseDownX);
      }

      // 构建y轴滚动条
      this.y.el = document.getElementById(this.y.id);
      if (!this.y.el) {
        this.y.el = document.createElement('div');
        this.y.el.setAttribute('id', this.y.id);
        document.body.appendChild(this.y.el);
        this.y.el.addEventListener('mousedown', this.onMouseDownY);
      }

      // 轮训定期检查滚动条展示状态（因为页面内容变化没有可用的钩子）
      if (!this.timingShow) {
        this.timingShow = window.setInterval(() => {
          this.show('x', true);
          this.show('y', true);
        }, 500);

        // 监听滚动事件
        document.addEventListener('scroll', this.onScroll);
      }
    };

    setTimeout(buildScrollbar, 500);
  }

  /**
   * 注销滚动条相关事件
   */
  destroyed() {
    if (this.timingShow) {
      document.removeEventListener('scroll', this.onScroll);
      this.x.el.removeEventListener('mousedown', this.onMouseDownX);
      this.x.el.remove();
      this.y.el.removeEventListener('mousedown', this.onMouseDownY);
      this.y.el.remove();
      window.clearInterval(this.timingShow);
      this.timingShow = null;
      document.body.removeAttribute('data-visual-scrollbar');
    }
  }
};

export default new Scrollbar();
